import { useState, useEffect, useRef } from "react"
import { Link } from "react-router-dom"
import { useSeoMeta } from "@unhead/react"
import {ArrowRight, ChevronDown, ChevronLeft, ChevronRight, HeartHandshake, HelpCircle, Leaf, MoonStar, Sparkles, Heart, ShieldCheck, MapPin, Pause, Play} from 'lucide-react'
import { calendlyUrl, faqDoula, images, practitionerName, prestations, siteName, testimonials } from "@/data/site"
import GoogleReviews from "@/components/GoogleReviews"

function Home() {
  useSeoMeta({
    title: "Doula à Plouay et Lorient | Au Creux de Maman",
    description: "Maïwenn Keraudran, doula à Plouay (56). Accompagnement périnatal, massages et ateliers bébé à domicile jusqu'à Lorient et le Morbihan.",
    ogTitle: "Doula à Plouay et Lorient | Au Creux de Maman",
    ogDescription: "Accompagnement pré-conception, grossesse, naissance et post-partum à Plouay, Lorient et dans un rayon de 35 km.",
    ogImage: "https://static.lumi.new/material/d0/d0528283e7d412945335571d147154bf.jpg",
    twitterCard: "summary_large_image",
  })

  return (
    <div>
      {/* Hero Header */}
      <section className="relative min-h-[90vh] overflow-hidden">
        <img
          src={images.hero}
          alt="Femme enceinte dans la lumière du matin"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#2c2018]/85 via-[#3c2f25]/55 to-transparent" />
        <div className="relative mx-auto flex min-h-[90vh] max-w-6xl flex-col justify-end px-5 pb-16 pt-28 md:justify-center md:px-8 md:pb-24">
          <div className="inline-flex w-fit items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-1.5 backdrop-blur-md">
            <Sparkles size={13} className="text-[#e8c9b8]" />
            <span className="text-[11px] font-medium uppercase tracking-[0.25em] text-[#f7efe9]">
              Doula & Accompagnante Périnatale à Plouay
            </span>
          </div>
          <h1 className="mt-6 max-w-4xl font-display text-5xl leading-[0.98] text-[#fcf9f5] md:text-7xl font-medium">
            Doula à Plouay : <br />
            <span className="italic font-normal text-[#e8c9b8]">accompagnement périnatal jusqu'à Lorient, Plouray et Pontivy</span>
          </h1>
          <div className="mt-6 max-w-xl space-y-2 text-[#ede1d6]">
            <p className="font-display text-2xl italic text-[#f7efe9] md:text-3xl">
              « Remettre la femme au centre du village. »
            </p>
            <p className="text-base leading-relaxed text-[#ede1d6] md:text-lg">
              Accompagner la préconception, la grossesse, la naissance et le post-partum avec écoute, douceur et sans jugement.
            </p>
          </div>
          <div className="mt-8 flex flex-wrap gap-4">
            <a
              href={calendlyUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-[#b85c38] to-[#9d4c2e] px-7 py-3.5 text-xs font-semibold uppercase tracking-[0.16em] text-white shadow-warm-md transition-all duration-300 hover:scale-[1.02] hover:shadow-warm-lg"
            >
              <span>Prendre rendez-vous</span>
              <ArrowRight size={14} />
            </a>
            <Link
              to="/prestations-maman"
              className="inline-flex items-center gap-2 rounded-full border border-white/40 bg-white/10 px-6 py-3.5 text-xs font-medium uppercase tracking-[0.16em] text-white backdrop-blur-sm transition-all hover:bg-white/20"
            >
              Découvrir les soins
            </Link>
          </div>

          <div className="mt-12 flex flex-wrap items-center gap-6 border-t border-white/15 pt-6 text-xs text-[#e0d0c4]">
            <div className="flex items-center gap-2">
              <MapPin size={15} className="text-[#e8c9b8]" />
              <span>19 rue Louis Le Moenic, Inguiniel · 35 km aux alentours</span>
            </div>
            <div className="flex items-center gap-2">
              <Heart size={15} className="text-[#e8c9b8]" />
              <span>Espace cocooning & écoute inconditionnelle</span>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="mx-auto grid max-w-6xl gap-10 px-5 py-20 md:grid-cols-[0.9fr_1.1fr] md:px-8">
        <div>
          <div className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.28em] text-[#b85c38]">
            <Heart size={13} />
            <span>Une présence continue</span>
          </div>
          <h2 className="mt-3 font-display text-4xl leading-tight text-[#2d221b] md:text-5xl">
            Ni soignante, ni amie :
            <span className="italic text-[#9d4c2e]"> une alliée</span>
          </h2>
        </div>
        <div className="space-y-5 text-base leading-relaxed text-[#5e4b3e]">
          <p className="font-medium text-[#3c2f25]">
            Je suis Maïwenn, doula et accompagnante périnatale à Plouay. Je vous offre un espace d'écoute et un soutien pratique avant et pendant la grossesse, autour de la naissance et durant les premiers mois avec bébé. Je vous reçois dans mon espace à Inguiniel, me déplace à domicile dans un rayon de 35 km et propose aussi des séances en visio.
          </p>
          <p>
            L'accompagnante périnatale offre un fil rouge précieux : une personne de confiance qui vous connaît, qui reste
            disponible, et qui protège votre bulle et votre rythme dans un moment où tout se transforme.
          </p>
          <p>
            J'interviens en complémentarité de votre équipe médicale et sage-femme. Je n'effectue aucun acte médical :
            je soutiens votre corps, vos émotions, vos choix souverains et le lien d'attachement avec votre bébé.
          </p>
          <div className="pt-2">
            <Link
              to="/a-propos"
              className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-[#6f4b3e] transition-colors hover:text-[#b85c38]"
            >
              <span>Découvrir mon histoire</span>
              <ArrowRight size={15} />
            </Link>
          </div>
        </div>
      </section>

      {/* 3 Pillars */}
      <section className="border-y border-[#e2d5c8] bg-[#ebe0d3]/60 py-16">
        <div className="mx-auto grid max-w-6xl gap-6 px-5 md:grid-cols-3 md:px-8">
          {[
            { icon: HeartHandshake, title: "Une relation", text: "Nous construisons un lien de confiance pour que vous puissiez vous sentir libre de vous exprimer sans retenues." },
            { icon: Leaf, title: "Un rythme", text: "Rien n'est imposé. Chaque rencontre s'adapte à votre histoire, vos doutes, vos souhaits et vos ressources." },
            { icon: MoonStar, title: "Un nid", text: "Après la naissance, je prends soin de vous au cœur du foyer pour que le post-partum soit un havre de paix." },
          ].map((item) => (
            <article
              key={item.title}
              className="group rounded-3xl border border-[#dfd0c2] bg-[#fbf7f2] p-8 shadow-warm-xs transition-all duration-300 hover:shadow-warm-md hover:-translate-y-1"
            >
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#efe1d4] text-[#b85c38] transition-colors group-hover:bg-[#b85c38] group-hover:text-white">
                <item.icon size={24} />
              </div>
              <h3 className="mt-5 font-display text-2xl font-medium text-[#2d221b]">{item.title}</h3>
              <p className="mt-3 text-sm leading-relaxed text-[#685548]">{item.text}</p>
            </article>
          ))}
        </div>
      </section>

      {/* Prestations Section */}
      <section className="mx-auto max-w-6xl px-5 py-20 md:px-8">
        <div className="mb-12 flex flex-col justify-between gap-6 md:flex-row md:items-end">
          <div>
            <span className="text-xs font-semibold uppercase tracking-[0.28em] text-[#b85c38]">
              Prestations & Rituels
            </span>
            <h2 className="mt-2 font-display text-4xl text-[#2d221b] md:text-5xl">
              Des soins pensés pour vous entourer
            </h2>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link
              to="/accompagnement-doula"
              className="rounded-full border border-[#d6c4b6] bg-white/80 px-4 py-2 text-xs uppercase tracking-[0.14em] text-[#6f4b3e] transition-colors hover:border-[#b85c38] hover:text-[#b85c38]"
            >
              Accompagnement Doula
            </Link>
            <Link
              to="/prestations-maman"
              className="rounded-full border border-[#d6c4b6] bg-white/80 px-4 py-2 text-xs uppercase tracking-[0.14em] text-[#6f4b3e] transition-colors hover:border-[#b85c38] hover:text-[#b85c38]"
            >
              Prestations Maman
            </Link>
            <Link
              to="/prestations-bebe"
              className="rounded-full border border-[#d6c4b6] bg-white/80 px-4 py-2 text-xs uppercase tracking-[0.14em] text-[#6f4b3e] transition-colors hover:border-[#b85c38] hover:text-[#b85c38]"
            >
              Prestations Bébé
            </Link>
          </div>
        </div>

        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {prestations.map((item) => (
            <Link
              key={item.id}
              to={item.id === "doula" ? "/accompagnement-doula" : "/prestations-maman"}
              className="group flex flex-col justify-between overflow-hidden rounded-[28px] border border-[#e2d5c8] bg-[#fbf7f2] shadow-warm-xs transition-all duration-300 hover:shadow-warm-md hover:-translate-y-1"
            >
              <div>
                <div className="aspect-square w-full overflow-hidden bg-stone-100">
                  <img
                    src={item.image}
                    alt={item.title}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
                <p className="px-6 pt-2 text-[10px] italic text-[#8c6d58]">Photo non contractuelle</p>
                <div className="p-6 pt-3">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[#b85c38]">{item.kicker}</p>
                  <h3 className="mt-2 font-display text-2xl font-medium text-[#2d221b]">{item.title}</h3>
                  <p className="mt-3 whitespace-pre-line text-xs leading-relaxed text-[#685548] line-clamp-3">{item.summary}</p>
                </div>
              </div>
              <div className="border-t border-[#eee2d6] p-6 pt-4">
                <p className="font-display text-xl font-semibold text-[#8a4225]">
                  {item.pricing.length > 1 ? `Dès ${item.pricing[0].price}` : item.pricing[0].price}
                </p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Tarifs / Panorama */}
      <section className="relative overflow-hidden">
        <img
          src={images.salon}
          alt="Intérieur lumineux et apaisant"
          loading="lazy"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-[#2b1f17]/75 backdrop-blur-[2px]" />
        <div className="relative mx-auto grid max-w-6xl gap-8 px-5 py-24 md:grid-cols-2 md:px-8">
          <div>
            <p className="text-xs uppercase tracking-[0.28em] text-[#e8c9b8]">Tarifs & Formules</p>
            <h2 className="mt-3 font-display text-4xl text-white md:text-5xl">
              Choisir le soin qui résonne en vous
            </h2>
            <p className="mt-4 max-w-md text-sm leading-relaxed text-[#e0d0c4]">
              Chaque accompagnement est modulable selon vos besoins spécifiques. Possibilité de régler en plusieurs fois.
            </p>
          </div>
          <div className="space-y-4">
            {prestations.map((item) => (
              <Link
                key={item.id}
                to={item.id === "doula" ? "/accompagnement-doula" : "/prestations-maman"}
                className="flex items-center justify-between gap-4 border-b border-white/15 pb-4 text-white transition-colors hover:text-[#e8c9b8]"
              >
                <div>
                  <p className="font-display text-2xl font-medium">{item.title}</p>
                  <p className="text-xs text-[#d6c4b6]">
                    {item.pricing.length > 1 ? `À partir de ${item.pricing[0].label.toLowerCase()}` : item.pricing[0].label}
                  </p>
                </div>
                <p className="shrink-0 font-display text-2xl font-semibold text-[#e8c9b8] tabular-nums whitespace-nowrap">
                  {item.pricing.length > 1 ? `Dès ${item.pricing[0].price}` : item.pricing[0].price}
                </p>
              </Link>
            ))}
            <div className="mt-6 flex flex-wrap gap-3 pt-2">
              <Link
                to="/accompagnement-doula"
                className="inline-flex rounded-full bg-[#fbf7f2] px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.16em] text-[#3c2f25] shadow transition hover:bg-white hover:scale-105"
              >
                Accompagnement Doula
              </Link>
              <Link
                to="/prestations-maman"
                className="inline-flex rounded-full border border-white/30 bg-white/5 px-5 py-2.5 text-xs font-medium uppercase tracking-[0.16em] text-white hover:bg-white/15"
              >
                Prestations Maman
              </Link>
              <Link
                to="/prestations-bebe"
                className="inline-flex rounded-full border border-white/30 bg-white/5 px-5 py-2.5 text-xs font-medium uppercase tracking-[0.16em] text-white hover:bg-white/15"
              >
                Prestations Bébé
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Témoignages */}
      <TestimonialsSection items={testimonials} />

      {/* Avis Google dynamiques */}
      <GoogleReviews enableJsonLd />

      {/* Section FAQ */}
      <section className="border-t border-[#dfd0c2] bg-[#ebe0d3]/50 py-20">
        <div className="mx-auto max-w-4xl px-5 md:px-8">
          <div className="text-center">
            <div className="inline-flex items-center gap-2 rounded-full border border-[#d6c4b6] bg-[#fbf7f2] px-4 py-1.5 text-xs uppercase tracking-[0.22em] text-[#b85c38] shadow-sm">
              <HelpCircle size={15} />
              <span>Questions fréquentes</span>
            </div>
            <h2 className="mt-4 font-display text-4xl text-[#2d221b] md:text-5xl">
              Tout ce que vous souhaitez savoir
            </h2>
            <p className="mx-auto mt-3 max-w-xl text-sm text-[#685548]">
              Des réponses claires pour comprendre le rôle d'une doula et le déroulé de mes accompagnements.
            </p>
          </div>

          <div className="mt-12 space-y-4">
            {faqDoula.map((item, index) => (
              <FaqItem key={index} question={item.question} answer={item.answer} defaultOpen={index === 0} />
            ))}
          </div>

          <div className="mt-10 rounded-2xl border border-[#dfd0c2] bg-[#fbf7f2] p-8 text-center shadow-warm-xs">
            <p className="font-display text-2xl text-[#2d221b]">Vous avez une autre question ?</p>
            <p className="mt-2 text-sm text-[#685548]">
              Je réponds avec plaisir à toutes vos interrogations avec bienveillance.
            </p>
            <Link
              to="/contact"
              className="mt-5 inline-flex items-center gap-2 rounded-full bg-[#6f4b3e] px-7 py-3 text-xs uppercase tracking-[0.14em] text-white transition hover:bg-[#5a3c32]"
            >
              <span>Me poser une question</span>
              <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* Banner / Rencontre */}
      <section className="mx-auto mb-20 grid max-w-6xl overflow-hidden rounded-[32px] border border-[#4a3528] bg-[#422e23] shadow-warm-lg md:grid-cols-2">
        <img
          src={images.linen}
          alt="Linge, lavande et objets de naissance"
          loading="lazy"
          className="h-72 w-full object-cover md:h-full"
        />
        <div className="flex flex-col justify-center px-8 py-12 text-[#f7efe9] md:px-12">
          <p className="text-xs uppercase tracking-[0.28em] text-[#e8c9b8]">Premier pas</p>
          <h2 className="mt-3 font-display text-4xl text-white">Une rencontre offerte, pour sentir si le lien est juste.</h2>
          <p className="mt-4 text-sm leading-relaxed text-[#ded0c6]">
            Quinze minutes en visio afin d'échanger sur vos attentes et mon fonctionnement. Vous repartez avec des repères, même si nous ne continuons pas ensemble.
          </p>
          <Link
            to="/contact"
            className="mt-8 inline-flex w-fit items-center gap-2 rounded-full bg-[#fbf7f2] px-7 py-3.5 text-xs font-semibold uppercase tracking-[0.16em] text-[#3c2f25] shadow transition hover:bg-white hover:scale-105"
          >
            <span>Écrire à {practitionerName.split(" ")[0]}</span>
            <ArrowRight size={14} />
          </Link>
        </div>
      </section>
    </div>
  )
}

function TestimonialsSection({ items }: { items: typeof testimonials }) {
  const [currentIndex, setCurrentIndex] = useState(0)
  const [isPaused, setIsPaused] = useState(false)
  const total = items.length
  const timerRef = useRef<number | null>(null)

  // Défilement automatique
  useEffect(() => {
    if (isPaused) return
    timerRef.current = window.setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % total)
    }, 4500)

    return () => {
      if (timerRef.current) clearInterval(timerRef.current)
    }
  }, [isPaused, total])

  const handleNext = () => {
    setCurrentIndex((prev) => (prev + 1) % total)
  }

  const handlePrev = () => {
    setCurrentIndex((prev) => (prev - 1 + total) % total)
  }

  const active = items[currentIndex]
  const initials = active.name.charAt(0).toUpperCase()

  return (
    <section className="relative overflow-hidden border-y border-[#e2d5c8] bg-[#ebe0d3]/40 py-20">
      <div className="mx-auto max-w-5xl px-5 md:px-8">
        <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.28em] text-[#b85c38]">Paroles de familles</p>
            <h2 className="mt-2 font-display text-4xl text-[#2d221b] md:text-5xl">Ce qu'elles en disent</h2>
            <p className="mt-2 text-sm text-[#685548]">
              Des retours doux et sincères sur nos moments de partage.
            </p>
          </div>

          <button
            type="button"
            onClick={() => setIsPaused((prev) => !prev)}
            title={isPaused ? "Reprendre le défilement" : "Mettre en pause"}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[#d6c4b6] bg-[#fbf7f2] text-[#6f4b3e] shadow-sm transition hover:border-[#b85c38] hover:text-[#b85c38]"
            aria-label={isPaused ? "Reprendre le défilement" : "Mettre en pause"}
          >
            {isPaused ? <Play size={15} /> : <Pause size={15} />}
          </button>
        </div>

        {/* Avis en vedette (spotlight éditorial) */}
        <div className="relative mt-14 flex items-center gap-3 md:gap-8">
          <button
            type="button"
            onClick={handlePrev}
            className="hidden h-11 w-11 shrink-0 items-center justify-center rounded-full border border-[#d6c4b6] bg-[#fbf7f2] text-[#6f4b3e] shadow-sm transition hover:border-[#b85c38] hover:text-[#b85c38] md:flex"
            aria-label="Avis précédent"
          >
            <ChevronLeft size={19} />
          </button>

          <div className="relative flex-1 overflow-hidden rounded-[32px] border border-[#dfd0c2] bg-[#fbf7f2] px-7 py-10 shadow-warm-md md:px-16 md:py-14">
            <span className="pointer-events-none absolute -top-10 left-5 select-none font-serif text-[150px] leading-none text-[#e8c9b8]/40 md:left-10">
              “
            </span>

            <div key={currentIndex} className="relative animate-testimonial">
              <p className="relative font-display text-xl italic leading-relaxed text-[#2d221b] md:text-[27px]">
                {active.quote}
              </p>

              <div className="mt-8 flex items-center gap-4 border-t border-[#eee2d6] pt-6">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-[#b85c38] to-[#9d4c2e] font-display text-lg text-white">
                  {initials}
                </div>
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-[#2d221b]">{active.name}</p>
                  <p className="truncate text-[11px] uppercase tracking-[0.14em] text-[#8c6d58]">{active.detail}</p>
                </div>
                <span className="ml-auto shrink-0 font-display text-sm text-[#b89f8b] tabular-nums">
                  {String(currentIndex + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}
                </span>
              </div>
            </div>
          </div>

          <button
            type="button"
            onClick={handleNext}
            className="hidden h-11 w-11 shrink-0 items-center justify-center rounded-full border border-[#d6c4b6] bg-[#fbf7f2] text-[#6f4b3e] shadow-sm transition hover:border-[#b85c38] hover:text-[#b85c38] md:flex"
            aria-label="Avis suivant"
          >
            <ChevronRight size={19} />
          </button>
        </div>

        {/* Navigation mobile */}
        <div className="mt-6 flex items-center justify-center gap-3 md:hidden">
          <button
            type="button"
            onClick={handlePrev}
            className="flex h-10 w-10 items-center justify-center rounded-full border border-[#d6c4b6] bg-[#fbf7f2] text-[#6f4b3e] shadow-sm transition hover:border-[#b85c38] hover:text-[#b85c38]"
            aria-label="Avis précédent"
          >
            <ChevronLeft size={18} />
          </button>
          <button
            type="button"
            onClick={handleNext}
            className="flex h-10 w-10 items-center justify-center rounded-full border border-[#d6c4b6] bg-[#fbf7f2] text-[#6f4b3e] shadow-sm transition hover:border-[#b85c38] hover:text-[#b85c38]"
            aria-label="Avis suivant"
          >
            <ChevronRight size={18} />
          </button>
        </div>

        {/* Indicateur de progression façon « stories » */}
        <div className="mx-auto mt-10 flex max-w-md items-center gap-1.5">
          {items.map((_, idx) => (
            <button
              key={idx}
              type="button"
              onClick={() => setCurrentIndex(idx)}
              aria-label={`Aller à l'avis ${idx + 1}`}
              className="relative h-1 flex-1 overflow-hidden rounded-full bg-[#dfd0c2]"
            >
              {idx === currentIndex ? (
                <span
                  key={currentIndex}
                  className="testimonial-progress-bar absolute inset-y-0 left-0 block rounded-full bg-[#b85c38]"
                  style={{ animationPlayState: isPaused ? "paused" : "running" }}
                />
              ) : idx < currentIndex ? (
                <span className="absolute inset-0 block rounded-full bg-[#b85c38]/40" />
              ) : null}
            </button>
          ))}
        </div>
      </div>
    </section>
  )
}

function FaqItem({ question, answer, defaultOpen = false }: { question: string; answer: string; defaultOpen?: boolean }) {
  const [isOpen, setIsOpen] = useState(defaultOpen)

  return (
    <div className="overflow-hidden rounded-2xl border border-[#dfd0c2] bg-[#fbf7f2] shadow-warm-xs transition-shadow hover:shadow-warm-sm">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex w-full items-center justify-between gap-4 p-6 text-left transition hover:text-[#b85c38]"
        aria-expanded={isOpen}
      >
        <span className="font-display text-xl font-medium text-[#2d221b]">{question}</span>
        <span
          className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#efe1d4] text-[#6f4b3e] transition-transform duration-200 ${
            isOpen ? "rotate-180 bg-[#b85c38] text-white" : ""
          }`}
        >
          <ChevronDown size={18} />
        </span>
      </button>
      {isOpen ? (
        <div className="border-t border-[#ede0d2] px-6 pb-6 pt-3 text-sm leading-relaxed text-[#685548]">
          <p>{answer}</p>
        </div>
      ) : null}
    </div>
  )
}

export default Home
